// src/components/shared/CommentSection.js
import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import Button from "../ui/Button";
import { Heart } from "lucide-react";

const Comment = ({ comment }) => {
  const { currentUser } = useAuth();
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(comment.likes || 0);
  const [replies, setReplies] = useState(comment.replies || []);
  const [replying, setReplying] = useState(false); // Is the reply box open?
  const [newReply, setNewReply] = useState("");

  const handleLike = () => {
    setIsLiked(!isLiked);
    setLikeCount(isLiked ? likeCount - 1 : likeCount + 1);
  };

  const handleReplySubmit = (e) => {
    e.preventDefault();
    if (!newReply.trim() || !currentUser) return;
    const newReplyObject = {
      id: `r${Date.now()}`,
      author: {
        name: currentUser.fullName,
        avatarUrl: currentUser.profilePictureUrl,
      },
      text: newReply,
      timestamp: "Just now",
      likes: 0,
    };
    setReplies([...replies, newReplyObject]);
    setNewReply("");
    setReplying(false);
  };

  return (
    <div className="flex items-start space-x-3">
      <img
        src={comment.author.avatarUrl}
        alt={comment.author.name}
        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
      />
      <div className="flex-1">
        <div className="bg-slate-100 rounded-xl p-3">
          <p className="font-semibold text-xs text-slate-800">
            {comment.author.name}
          </p>
          <p className="text-xs text-slate-600">{comment.text}</p>
        </div>
        <div className="flex items-center space-x-4 pl-3 pt-1">
          <button
            onClick={handleLike}
            className={`flex items-center space-x-1 text-xs transition-colors ${
              isLiked
                ? "text-red-500 font-semibold"
                : "text-slate-500 font-medium hover:text-red-500"
            }`}
          >
            <Heart size={14} fill={isLiked ? "currentColor" : "none"} />
            <span>{likeCount > 0 ? likeCount : "Like"}</span>
          </button>
          <button
            onClick={() => setReplying(!replying)} // Toggle the reply input
            className="text-xs font-medium text-slate-500 hover:text-primary transition-colors"
          >
            Reply
          </button>
        </div>

        <div className="pl-3 pt-3 space-y-3 border-l-2 border-slate-200 ml-4 mt-2">
          {replies.map((reply) => (
            <Comment key={reply.id} comment={reply} />
          ))}

          {replying && currentUser && (
            <form
              onSubmit={handleReplySubmit}
              className="flex items-center space-x-2 pt-2"
            >
              <img
                src={currentUser.profilePictureUrl}
                alt="Your avatar"
                className="w-6 h-6 rounded-full object-cover"
              />
              <input
                type="text"
                value={newReply}
                onChange={(e) => setNewReply(e.target.value)}
                placeholder="Write a reply..."
                className="flex-1 w-1 h-6 text-sm p-1.5 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
                autoFocus
              />
              <Button
                type="submit"
                size="sm"
                className="!px-2.5 !py-1 text-xs"
                disabled={!newReply.trim()}
              >
                Send
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// The main CommentSection component remains the same
const CommentSection = ({ initialComments = [] }) => {
  const { currentUser } = useAuth();
  const [comments, setComments] = useState(initialComments);
  const [newComment, setNewComment] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newComment.trim() || !currentUser) return;
    const newCommentObject = {
      id: `c${Date.now()}`,
      author: {
        name: currentUser.fullName,
        avatarUrl: currentUser.profilePictureUrl,
      },
      text: newComment,
      timestamp: "Just now",
      likes: 0,
      replies: [],
    };
    setComments([...comments, newCommentObject]);
    setNewComment("");
  };

  return (
    <div className="p-4 space-y-4">
      <div className="space-y-4">
        {comments.map((comment) => (
          <Comment key={comment.id} comment={comment} />
        ))}
      </div>
      {currentUser && (
        <form
          onSubmit={handleSubmit}
          className="flex space-x-3 pt-4 border-t border-slate-200"
        >
          <img
            src={currentUser.profilePictureUrl}
            alt="Your avatar"
            className="w-8 h-8 rounded-full object-cover"
          />

          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Write a comment..."
            className="flex-1 p-2 w-1 h-8 bg-slate-100 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <Button type="submit" size="sm" disabled={!newComment.trim()}>
            Post
          </Button>
        </form>
      )}
    </div>
  );
};

export default CommentSection;
